import { Button, GenericModal } from 'moar-interactive-shared';
import React, { useEffect, useLayoutEffect, useRef, useState, Fragment, useCallback } from 'react';

import AudioPlayer from './AudioPlayer';
import { slideOut, fadeOut } from './../animations/modalAnimations';
import slideAnimation from './../animations/slideAnimation';
import useWindowSize from '../hooks/useWindowSize';
import isMobile from '../utils/isMobile';

const Slide = ({
  isActive,
  choices = [],
  nextSlide,
  setSlideIsAnimating,
  question,
  audio,
  hotspots,
  image_alt_text,
  image_position,
  mobile_hotspots,
  pageReady,
}) => {
  const [timeline] = useState();
  const [hasAnimated, setHasAnimated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const textRef = useRef();
  const imageRef = useRef();
  const hotspotRef = useRef();
  const windowSize = useWindowSize();

  const timelineRef = useRef(null);

  const onAnimationBegin = useCallback(() => {
    setSlideIsAnimating(true);
  }, [setSlideIsAnimating]);

  const onAnimationEnd = useCallback(() => {
    setSlideIsAnimating(false);
    setHasAnimated(true);
  }, [setSlideIsAnimating]);

  useEffect(() => {
    setSlideIsAnimating(false);
  }, [isActive, setSlideIsAnimating]);

  useLayoutEffect(() => {
    if (isActive && hasAnimated) {
      [textRef, imageRef, hotspotRef].forEach((ref) => {
        if (ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = '';
        }
      });
      return;
    }

    if (isActive && pageReady && !hasAnimated) {
      const anim = slideAnimation(
        textRef,
        imageRef,
        hotspotRef,
        onAnimationBegin,
        onAnimationEnd,
        windowSize.width,
      );

      timelineRef.current = anim;
      timelineRef.current?.play();
    }

    return () => {
      if (timelineRef.current) {
        timelineRef.current.pause();
      }
    };
  }, [isActive, pageReady, hasAnimated, onAnimationBegin, onAnimationEnd, windowSize.width]);

  useEffect(() => {
    if (isActive && timeline && pageReady) {
      timeline.play();
    }
  }, [isActive, timeline, pageReady]);

  const nextPanel = () => {
    const modal = document.querySelector('.optionModal .modal');
    nextSlide();
    const modalObj = { current: modal };
    slideOut(modalObj, () => setShowModal(false));
  };

  const closeModal = () => {
    const modal = document.querySelector('.optionModal .modal');
    const modalObj = { current: modal };
    fadeOut(modalObj, () => setShowModal(false));
  };

  const classArray = () => {
    const classes = ['slide', 'slide--options'];
    if (isActive) classes.push('slide--active');
    if (image_position === 'right') classes.push('slide--invert');
    return classes.join(' ');
  };

  const generateOption = (data) => {
    if (!data) return;
    return (
      <Fragment>
        <div className="modal__content">
          <div className="selection">
            <h2 className="selection__title">{data.answer_title}</h2>
            <div className="selection__text">
              <p>{data.answer_text}</p>
            </div>
            {data.audio && (
              <div className="selection__audio">
                <AudioPlayer audioFile={data.audio} />
              </div>
            )}
          </div>
          <div className="optionButtons">
            <Button iconPosition="before" darkBg={true} label="Back" handleClick={closeModal} />
            <Button darkBg={true} label="Continue Story" handleClick={nextPanel} />
          </div>
        </div>
      </Fragment>
    );
  };

  const hotspotImage = isMobile() ? mobile_hotspots?.image : hotspots?.image;

  return (
    <div className={classArray()}>
      <div className="slide__content" inert={isActive ? undefined : true}>
        <div className="slide__image">
          <div className="content imageContent">
            <div className="slide__hotspots">
              <div className="content" ref={hotspotRef} />
            </div>
            <div className="image" ref={imageRef}>
              <img
                className="desktopImage"
                alt={image_alt_text || 'Slide background'}
                src={hotspotImage}
              />
            </div>
          </div>
        </div>

        <div className="slide__text" ref={textRef}>
          <h2>{question}</h2>

          <div className="options">
            {choices.map((choice, index) => (
              <div className="option" key={`choice-${index}`}>
                <p>{choice.summary}</p>
                <Button
                  basic={true}
                  label={choice.cta_text}
                  handleClick={() => {
                    setModalData(choice);
                    setShowModal(true);
                  }}
                />
              </div>
            ))}
          </div>

          {audio && (
            <div className="slide__audio">
              <AudioPlayer audioFile={audio} />
            </div>
          )}
        </div>
      </div>

      <GenericModal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        overlayName={'optionModal'}
      >
        {generateOption(modalData)}
      </GenericModal>
    </div>
  );
};

export default Slide;
